import { useEffect } from "react"
import { cn } from "lib/utils"
import type { CalendarEvent } from "../../types/agenda"
import { EVENT_COLORS, EVENT_COLORS_LIGHT, EVENT_TEXT_COLORS, EVENT_TIME_COLORS } from "../../constants/agenda"
import { formatTime } from "../../utils/agenda"

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"]

interface CalendarMonthViewProps {
  currentDate: Date
  events: CalendarEvent[]
  onEventClick?: (event: CalendarEvent) => void
  onDayClick?: (date: Date) => void
  onEmptyCellClick?: (date: Date) => void
  highlightedEventId?: number | null
}

export function CalendarMonthView({ currentDate, events, onEventClick, onDayClick, onEmptyCellClick, highlightedEventId }: CalendarMonthViewProps) {
  // Scroll-into-view sull'evento evidenziato (es. dopo click su notifica)
  useEffect(() => {
    if (!highlightedEventId) return
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-event-id="${highlightedEventId}"]`)
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 300)
    return () => clearTimeout(timer)
  }, [highlightedEventId])
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  // Get first day of month (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfMonth = new Date(year, month, 1)
  let firstDayWeekday = firstDayOfMonth.getDay()
  // Convert Sunday (0) to 7 for easier calculation (Monday = 1, Sunday = 7)
  firstDayWeekday = firstDayWeekday === 0 ? 7 : firstDayWeekday

  // Get last day of month
  const lastDayOfMonth = new Date(year, month + 1, 0)
  const daysInMonth = lastDayOfMonth.getDate()

  // Get days from previous month to show
  const daysFromPrevMonth = firstDayWeekday - 1
  const prevMonth = month === 0 ? 11 : month - 1
  const prevMonthYear = month === 0 ? year - 1 : year
  const lastDayOfPrevMonth = new Date(prevMonthYear, prevMonth + 1, 0).getDate()

  // Calculate total cells needed
  const totalCells = daysFromPrevMonth + daysInMonth
  const rows = Math.ceil(totalCells / 7)
  const totalDays = rows * 7

  // Build array of all days to display
  const days: Array<{
    date: Date
    dayNumber: number
    isCurrentMonth: boolean
    isToday: boolean
  }> = []

  // Previous month days
  for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
    const dayNumber = lastDayOfPrevMonth - i
    const date = new Date(prevMonthYear, prevMonth, dayNumber)
    days.push({
      date,
      dayNumber,
      isCurrentMonth: false,
      isToday: false,
    })
  }

  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(year, month, i)
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    days.push({
      date,
      dayNumber: i,
      isCurrentMonth: true,
      isToday,
    })
  }

  // Next month days
  const remainingCells = totalDays - days.length
  const nextMonth = month === 11 ? 0 : month + 1
  const nextMonthYear = month === 11 ? year + 1 : year
  for (let i = 1; i <= remainingCells; i++) {
    const date = new Date(nextMonthYear, nextMonth, i)
    days.push({
      date,
      dayNumber: i,
      isCurrentMonth: false,
      isToday: false,
    })
  }

  // Find the row that contains today (for week highlighting)
  const todayRowIndex = days.findIndex((d) => d.isToday)
  const todayRow = todayRowIndex >= 0 ? Math.floor(todayRowIndex / 7) : -1

  // Get events for a specific date
  const getEventsForDate = (date: Date) => {
    return events.filter((event) => {
      const eventDate = new Date(event.start_date)
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear()
      )
    })
  }

  return (
    <div aria-label="Calendario mensile" className="min-w-0 bg-[var(--hexa-card-bg)] rounded-lg overflow-hidden hexa-section-shadow border border-[rgba(106,77,255,0.25)]">
      {/* Day headers */}
      <div className="grid min-w-0 grid-cols-7 border-b border-[rgba(106,77,255,0.25)] bg-[var(--hexa-surface-elevated)]">
        {DAYS.map((day) => (
          <div
            key={day}
            className="py-2 text-center text-xs font-medium text-[var(--hexa-text-secondary)] uppercase tracking-wider border-r border-[rgba(106,77,255,0.25)] last:border-r-0"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid min-w-0 grid-cols-7">
        {days.map((day, index) => {
          const eventsForDay = getEventsForDate(day.date)
          const visibleEvents = eventsForDay.slice(0, 2)
          const remainingCount = eventsForDay.length - visibleEvents.length
          const colIndex = index % 7
          const isWeekend = colIndex >= 5
          const isInTodayRow = todayRow >= 0 && Math.floor(index / 7) === todayRow
          const dayTime = day.date.getTime()
          const todayTime = today.getTime()
          const isPast = !day.isToday && dayTime < todayTime

          return (
            <div
              key={`${day.date.toISOString()}-${index}`}
              className={cn(
                "min-w-0 min-h-[90px] p-0.5 sm:p-1.5 border-r border-b border-[rgba(106,77,255,0.25)] last:border-r-0 cursor-pointer transition-colors hover:bg-[rgba(106,77,255,0.08)]",
                index >= days.length - 7 && "border-b-0",
                !day.isCurrentMonth && !isPast && "bg-[var(--hexa-surface)]",
                isInTodayRow && day.isCurrentMonth && !isPast && "bg-[var(--hexa-purple)]/[0.03]",
                day.isToday && "bg-[var(--hexa-purple)]/[0.08] border-l-2 border-l-[var(--hexa-purple)]",
              )}
              style={isPast ? { backgroundColor: 'rgba(0, 0, 0, 0.15)' } : undefined}
              onClick={() => onEmptyCellClick?.(day.date)}
            >
              {/* Day number */}
              <div className="flex justify-between items-start mb-1">
                <button
                  type="button"
                  aria-label={`Mostra eventi del ${day.date.toLocaleDateString('it-IT')}`}
                  onClick={(event) => { event.stopPropagation(); onDayClick?.(day.date) }}
                  className={cn(
                    "text-xs font-medium inline-flex items-center justify-center rounded-full",
                    day.isToday
                      ? "w-6 h-6 bg-[var(--hexa-purple)] text-white font-bold"
                      : "w-5 h-5",
                    !day.isToday && day.isCurrentMonth && !isWeekend && !isPast && "text-[var(--hexa-text-primary)]",
                    !day.isToday && day.isCurrentMonth && (isWeekend || isPast) && "text-[var(--hexa-text-muted)]",
                    !day.isToday && !day.isCurrentMonth && "text-[var(--hexa-text-muted)]",
                  )}
                  style={day.isToday ? { boxShadow: "0 0 0 3px rgba(123, 63, 255, 0.25)" } : undefined}
                >
                  {day.dayNumber}
                </button>
              </div>

              {/* Events */}
              <div className="space-y-0.5">
                {visibleEvents.map((event) => {
                  const type = event.type || 'default'
                  const isTask = event.source === 'task'
                  const isOverdue = isTask && event.is_overdue
                  const isCompleted = isTask && event.is_completed

                  // Task: rosso se scaduto, ambra se non scaduto; altrimenti colori normali
                  let bgColor: string, borderColor: string, textColor: string, timeColor: string
                  if (isOverdue) {
                    bgColor = 'rgba(239, 68, 68, 0.30)'
                    borderColor = '#ef4444'
                    textColor = '#991b1b'
                    timeColor = 'rgba(153, 27, 27, 0.75)'
                  } else if (isTask) {
                    bgColor = 'rgba(245, 158, 11, 0.28)'
                    borderColor = '#f59e0b'
                    textColor = '#92400e'
                    timeColor = 'rgba(146, 64, 14, 0.75)'
                  } else {
                    bgColor = EVENT_COLORS_LIGHT[type] || EVENT_COLORS_LIGHT.default
                    borderColor = EVENT_COLORS[type] || EVENT_COLORS.default
                    textColor = EVENT_TEXT_COLORS[type] || EVENT_TEXT_COLORS.default
                    timeColor = EVENT_TIME_COLORS[type] || EVENT_TIME_COLORS.default
                  }

                  return (
                    <button
                      key={event.id}
                      data-event-id={event.id}
                      className={`min-w-0 w-full text-left px-0.5 sm:px-2 py-1 text-[9px] sm:text-[11px] font-medium overflow-hidden text-ellipsis whitespace-nowrap hover:opacity-80 transition-opacity ${highlightedEventId === event.id ? "task-highlight" : ""}`}
                      style={{
                        backgroundColor: bgColor,
                        borderLeft: `3px solid ${borderColor}`,
                        color: textColor,
                        opacity: isCompleted ? 0.45 : undefined,
                      }}
                      title={`${isTask ? '📋' : ''}${formatTime(event.start_time)} ${event.title}${isOverdue ? ' · Scaduto' : ''}`}
                      onClick={(e) => { e.stopPropagation(); onEventClick?.(event) }}
                    >
                      {event.start_time && (
                        <span className="block sm:inline text-[8px] sm:text-[11px]" style={{ color: timeColor }}>
                          {formatTime(event.start_time)}
                          {" "}
                        </span>
                      )}
                      <span className="block truncate sm:inline">{event.title}</span>
                    </button>
                  )
                })}

                {remainingCount > 0 && (
                  <button
                    aria-label={`Mostra tutti i ${eventsForDay.length} eventi del ${day.date.toLocaleDateString('it-IT')}`}
                    className="inline-flex max-w-full items-center justify-center bg-[var(--hexa-purple)]/20 rounded-full px-1 sm:px-2 py-0.5 text-[10px] font-semibold text-[var(--hexa-purple)] hover:bg-[var(--hexa-purple)]/30 transition-colors cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); onDayClick?.(day.date) }}
                  >
                    <span className="truncate">+{remainingCount} {remainingCount === 1 ? "altro" : "altri"}</span>
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
